import React, { useEffect } from "react";
import { useRecoilState } from "recoil";
import { add, isAfter, startOfDay } from "date-fns";
import { useForm } from "react-hook-form";
import { produce } from "immer";

import t from "lib/i18n";
import { getSmartCarnetSearchURL } from "lib/searchUrl";
import useConfig from "hooks/useConfig";
import { smartCarnetFormState } from "state";
import useLayout from "hooks/useLayout";
import DateTime from "models/DateTime";
import "styles/pages/other.scss";
import useGTM, { useGTMChange } from "hooks/useGTM";
import { trackSmartCarnetStartDate, search as trackSearch } from "lib/tracking";

import Button, { ButtonTypes } from "components/ui/Button";
import NewDatesSelector from "components/datetime/DatesSelector";
import Passengers from "components/Passengers";
import StationsSelector from "components/StationsSelector";
import citcMockStationList from "mocks/citc-stations";
import { TICKET_TYPES } from "consts";

const SmartCarnet = () => {
  const {
    redirectUrl,
    searchCallBack,
    defaultValues,
    mode,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
  } = useConfig();

  const [form, setForm] = useRecoilState(smartCarnetFormState); // retrieve the form's state from a global state

  const configStationFrom = defaultValues?.ticketTypes?.smartCarnet?.stationFrom;
  const configStationTo = defaultValues?.ticketTypes?.smartCarnet?.stationTo;
  const configFromDateTime = defaultValues?.ticketTypes?.smartCarnet?.fromDateTime;

  const methods = useForm({
    defaultValues: form ?? {
      stationFrom: configStationFrom,
      stationTo: configStationTo,
      adults: 1,
      fromDateTime: configFromDateTime
        ? new DateTime(`${configFromDateTime}T23:59:59`)
        : new DateTime().departNow(),
      ticketType: TICKET_TYPES.SMART_CARNET,
      toDateTime: null
    }
  });
  const { clearErrors, errors, handleSubmit, getValues, register, setValue, watch } = methods;
  const { isMobile } = useLayout(true);

  const { adults, fromDateTime, ticketType, toDateTime } = watch([
    "adults",
    "children",
    "fromDateTime",
    "stationFrom",
    "stationTo",
    "ticketType",
    "toDateTime"
  ]);
  useGTMChange(fromDateTime, trackSmartCarnetStartDate);

  useEffect(() => {
    register({ name: "adults" }, { required: true });
    register({ name: "stationFrom" }, { required: true });
    register({ name: "stationTo" }, { required: true });
    register({ name: "ticketType" }, { required: true });
    register({ name: "toDateTime" }, { required: false });
    register(
      { name: "fromDateTime" },
      {
        required: true,
        validate: {
          smartCarnetStartNoLater: value => {
            return !isAfter(
              startOfDay(getValues("fromDateTime").toDate()),
              add(new Date(), { months: 1 })
            );
          }
        }
      }
    );

    return () => {
      // Save the form state
      const nextForm = produce(form, () => getValues);

      setForm(nextForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGTM = useGTM();
  const onSubmit = data => {
    const urlSearch = getSmartCarnetSearchURL({
      data,
      redirectUrl,
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    });
    dispatchGTM(trackSearch());

    if (searchCallBack) {
      searchCallBack(urlSearch, data);
    } else {
      window.top.location.href = urlSearch;
    }
  };

  const setFieldValue = field => value => {
    setValue(field, value, true);

    if (field === "ticketType") {
      clearErrors("toDateTime");
    }
  };

  const onChangeFromDateTime = newValue => {
    setValue("fromDateTime", fromDateTime?.produce(newValue), true);
  };

  const onChangeToDateTime = newValue => {
    setValue("toDateTime", toDateTime?.produce(newValue), true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="otrl-jp__other__control-bar">
        <div className="otrl-jp__other__control-bar__stations">
          <StationsSelector
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            disableGeolocation
            disablePopularStations
            ticketType={ticketType}
            mockStationsList={citcMockStationList}
          />
        </div>
        {isMobile ? (
          <div className="otrl-jp__other__journey-type-and-dates">
            <NewDatesSelector
              errors={errors}
              fromDateTime={fromDateTime}
              onChangeFromDateTime={onChangeFromDateTime}
              onChangeToDateTime={onChangeToDateTime}
              ticketType={ticketType}
              toDateTime={toDateTime}
              maxDate={add(new Date(), { months: 1 })}
              isOther
            />
          </div>
        ) : (
          <NewDatesSelector
            errors={errors}
            fromDateTime={fromDateTime}
            onChangeFromDateTime={onChangeFromDateTime}
            onChangeToDateTime={onChangeToDateTime}
            ticketType={ticketType}
            toDateTime={toDateTime}
            maxDate={add(new Date(), { months: 1 })}
            isOther
          />
        )}
        {!isMobile && (
          <Passengers
            adults={adults}
            onChange={setFieldValue}
            errorRailcards={errors["railcards"]}
            disableRailcards
            disableInput
          />
        )}
        <Button
          className="otrl-jp__other__submit"
          text={t(isMobile ? "tickets.submitCarnet.long" : "tickets.submit")}
          type={ButtonTypes.SUBMIT}
        />
      </div>
      <div className="otrl-jp__other__smart-carnet-notification">
        {t("passengers.label.smart.carnet.mobile")}
      </div>
    </form>
  );
};

export default SmartCarnet;
